import { get as getCookie } from 'es-cookie'
import { useSelector } from 'react-redux'

export const AI_GENERATION_URL = 'https://ai-generation.minicoursegenerator.com'

const PORTAL_COLLECTION_TYPE = 1

const toId = value => (/^\d+$/.test(value) ? Number(value) : value)

// Where the interaction is being built (course, path or portal), saved with each build.
// Ids come from the URL; the redux collection is only trusted when it matches that id.
export const useInteractionMetadata = () => {
  const collection = useSelector(state => state.collection?.collection)

  return sessionId => {
    const [, root, id] = window.location.pathname.split('/')
    const metadata = { session_id: sessionId }

    if (root === 'mini-course' && id) {
      return { ...metadata, entity_type: 'course', entity_id: toId(id) }
    }

    if (root === 'collection' && id) {
      const isLoaded = String(collection?.id) === id
      const portalPageId = new URLSearchParams(window.location.search).get(
        'portalPage',
      )
      return {
        ...metadata,
        entity_type: !isLoaded
          ? 'collection'
          : collection.type === PORTAL_COLLECTION_TYPE
          ? 'portal'
          : 'path',
        entity_id: toId(id),
        ...(portalPageId && { portal_page_id: toId(portalPageId) }),
      }
    }

    return { ...metadata, entity_type: null, entity_id: null }
  }
}

export const BUILD_STAGES = [
  'Understanding your request',
  'Planning layout & structure',
  'Applying your colors',
  'Building components',
  'Adding interactions & logic',
  'Final checks',
]

export const BUILD_READY_LABEL = 'Ready. Loading preview…'

// Average HTML length of a new interaction, used as the expected length of a new build
export const NEW_BUILD_EXPECTED_LENGTH = 40000

const HTML_START = '##html_start##'
const HTML_END = '##html_end##'
const MAX_PERCENT_BEFORE_DONE = 95

// The build reaches the stage at index i once STAGE_TAGS[i] appears in the HTML
const STAGE_TAGS = [null, null, /<style/i, /<body/i, /<script/i, /<\/html>/i]

export const createInitialBuildProgress = () => ({
  stageIndex: 0,
  percent: 0,
  done: false,
})

// Buffers the raw stream and derives the stage and percent from it.
// The buffered HTML is never rendered.
export const createBuildProgressTracker = expectedLength => {
  let response = ''
  let progress = createInitialBuildProgress()

  return {
    push(chunk) {
      response += chunk

      const htmlStart = response.indexOf(HTML_START)
      if (htmlStart === -1) return progress

      const htmlEnd = response.indexOf(HTML_END, htmlStart)
      const html = response.slice(
        htmlStart + HTML_START.length,
        htmlEnd === -1 ? undefined : htmlEnd,
      )

      let stageIndex = Math.max(progress.stageIndex, 1)
      for (let i = STAGE_TAGS.length - 1; i > stageIndex; i--) {
        if (STAGE_TAGS[i].test(html)) {
          stageIndex = i
          break
        }
      }

      const percent = Math.min(
        MAX_PERCENT_BEFORE_DONE,
        Math.floor((html.length / Math.max(expectedLength, 1)) * 100),
      )

      if (stageIndex !== progress.stageIndex || percent !== progress.percent) {
        progress = { stageIndex, percent, done: false }
      }
      return progress
    },
  }
}

export const createDoneBuildProgress = () => ({
  stageIndex: BUILD_STAGES.length,
  percent: 100,
  done: true,
})

// The AI service sends errors as a regular message section
export const getStreamErrorMessage = response => {
  const match = response.match(
    /##message_start##\s*((?:API |GPT-5 Processing )?Error:[\s\S]*?)(?:##message_end##|$)/,
  )
  return match ? match[1].trim() : null
}

export const fetchBuildResult = async buildId => {
  const response = await fetch(
    `${AI_GENERATION_URL}/api/buildResult/${encodeURIComponent(buildId)}`,
    {
      headers: {
        Authorization: `Bearer ${getCookie('token')}`,
      },
    },
  )

  if (!response.ok) {
    throw new Error(
      `Could not load the generated interaction (${response.status})`,
    )
  }

  const { html } = await response.json()
  if (!html || !html.trim()) {
    throw new Error('The generated interaction is empty')
  }
  return html
}
